"use client";

import { useEffect } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { reportClientError } from "@/lib/error-log";

/**
 * SectionErrorBoundary-Lücken-Audit (STATUS.md 2026-08-06): page.tsx lädt in einem
 * einzelnen `Promise.all` mehrere Supabase-Abfragen + FX-Kursumrechnungen
 * (`getExchangeRate`) ohne eigenes try/catch - ein Aussetzer riss bisher auf die
 * generische, unformatierte Next.js-Fehlerseite statt einer erklärenden Meldung.
 * Gleiches Muster wie app/screener/error.tsx.
 *
 * SiteHeader lebt seit dem Doppel-Header-Bugfix (Erik 2026-08-19) in
 * app/dividenden-tracker/layout.tsx, nicht mehr hier - siehe dortige Docstring.
 */
export default function DividendenTrackerError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[app/dividenden-tracker/error]", error.digest ?? "", error);
    reportClientError({ source: "app/dividenden-tracker/error", message: error.message, stack: error.stack });
  }, [error]);

  return (
    <main className="mx-auto flex max-w-[min(94vw,1200px)] flex-col items-center gap-8 px-4 py-24 text-center">
      <Card className="max-w-md">
        <CardContent className="pt-6">
          <p className="font-serif text-xl text-ink">Dividenden-Tracker gerade nicht abrufbar</p>
          <p className="mt-3 text-sm text-muted-foreground">
            Deine Dividendendaten konnten gerade nicht geladen werden. Bitte versuche es
            erneut oder probiere es in Kürze noch einmal.
          </p>
          <div className="mt-5 flex items-center justify-center gap-4">
            <Button onClick={reset} variant="outline">
              Erneut versuchen
            </Button>
            <Link href="/dashboard" className="text-sm font-medium text-secondary hover:text-ink">
              Zum Dashboard
            </Link>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
